import { Eye, RotateCcw, DollarSign, Package, User, FileText, Calendar, X, Truck, Printer, Download, Copy, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { GlobalFilterBar, FilterValues } from '../components/ui/GlobalFilterBar'
import { SaleDetailModal } from '../components/sales/modals/SaleDetailModal'
import { ReturnSaleModal } from '../components/sales/modals/ReturnSaleModal'
import { CancelSaleModal } from '../components/sales/modals/CancelSaleModal'
import { CancelCfdiModal } from '../components/sales/modals/CancelCfdiModal'
import { InvoiceSaleModal, DownloadInvoiceModal } from '../components/sales/modals/InvoiceSaleModal'
import { GlobalInvoiceWizard } from '../components/sales/modals/GlobalInvoiceWizard'
import { InvoicesHistoryModal } from '../components/sales/modals/InvoicesHistoryModal'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { CatalogSelect } from '../components/ui/CatalogSelect'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { Sale, salesService } from '../services/sales.service'
import { banksService, BankItem } from '../services/banks.service'
import { useEffect } from 'react'
import { formatShortTicket, copyToClipboard } from '../lib/utils'

export function Sales() {

  const offset = new Date().getTimezoneOffset() * 60000;
  const today = new Date(Date.now() - offset).toISOString().split('T')[0];

  const [filters, setFilters] = useState<FilterValues>({
    search: '',
    branch: '',
    status: 'all',
    seller: '',
    delivery: '',
    payment: 'all',
    bank: 'all',
    invoice: 'all',
    source: 'all',
    startDate: '',
    endDate: ''
  })

  const [page, setPage] = useState(1)

  const [banks, setBanks] = useState<BankItem[]>([])
  useEffect(() => {
    banksService.getItems({ all: true, is_active: true }).then(res => setBanks(res.data || res || []))
  }, [])
  const [selectedSale, setSelectedSale] = useState<any>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showReturnModal, setShowReturnModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [returnType, setReturnType] = useState<'partial' | 'total'>('partial')
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [showGlobalInvoiceWizard, setShowGlobalInvoiceWizard] = useState(false)
  const [showInvoicesHistoryModal, setShowInvoicesHistoryModal] = useState(false)
  const [printQueue, setPrintQueue] = useState<string | number | null>(null)
  // Estado para cancelación de CFDI standalone
  const [cancelCfdiTarget, setCancelCfdiTarget] = useState<{ cfdiId: string; saleId: string | number } | null>(null)

  const { data: salesData, isLoading } = useQuery({
    queryKey: [
      'sales',
      page,
      filters
    ],
    queryFn: () =>
      salesService.getSales({
        page,
        per_page: 10,
        search: filters.search || undefined,
        sucursal_id: filters.branch || undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        seller_id: filters.seller || undefined,
        delivery_user_id: filters.delivery || undefined,
        payment_method_id: filters.payment !== 'all' ? filters.payment : undefined,
        bank_name: filters.bank !== 'all' ? filters.bank : undefined,
        invoice_status: filters.invoice !== 'all' ? filters.invoice : undefined,
        date_from: filters.startDate || undefined,
        date_to: filters.endDate || undefined,
      }),
  })

  const salesList = salesData?.data || []
  const totalItems = salesData?.total || 0
  const currentPage = salesData?.current_page || 1
  const summary = salesData?.summary || {}
  const filterOptions = salesData?.filter_options || {}

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-700'
      case 'PARTIALLY_REFUNDED':
        return 'bg-yellow-100 text-yellow-700'
      case 'REFUNDED':
        return 'bg-red-100 text-red-700'
      case 'CANCELLED':
        return 'bg-slate-100 text-slate-500'
      default:
        return 'bg-slate-100 text-slate-700'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'Completada'
      case 'PARTIALLY_REFUNDED':
        return 'Devolución Parcial'
      case 'REFUNDED':
        return 'Devolución Total'
      case 'CANCELLED':
        return 'Cancelada'
      default:
        return status
    }
  }

  const openDetailModal = (sale: Sale, autoPrint = false) => {
    setSelectedSale(sale)
    if (autoPrint) setPrintQueue(sale.id)
    setShowDetailModal(true)
  }

  const openReturnModal = (sale: any, type: 'partial' | 'total') => {
    setSelectedSale(sale)
    setReturnType(type)
    setShowReturnModal(true)
  }

  const openCancelModal = (sale: any) => {
    setSelectedSale(sale)
    setShowCancelModal(true)
  }

  const openInvoiceModal = (sale: Sale) => {
    setSelectedSale(sale)
    setShowInvoiceModal(true)
  }

  const openDownloadModal = (sale: Sale) => {
    setSelectedSale(sale)
    setShowDownloadModal(true)
  }


  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Registro de Ventas
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {totalItems} ventas registradas
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowInvoicesHistoryModal(true)}
            className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-all hover:border-slate-300 hover:bg-slate-50 shrink-0 shadow-sm"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 group-hover:bg-slate-200 transition-colors">
              <FileText className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700">Historial Facturas</p>
              <p className="text-xs text-slate-500">Consulta y descargas</p>
            </div>
          </button>

          <button
            onClick={() => setShowGlobalInvoiceWizard(true)}
            className="group flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-left transition-all hover:border-primary/60 hover:bg-primary/10 shrink-0 shadow-sm"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 group-hover:bg-primary/25 transition-colors">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-primary">Factura Global</p>
              <p className="text-xs text-slate-500">Público en General</p>
            </div>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-50 p-2">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Ventas de hoy</p>
              <p className="text-2xl font-semibold text-slate-900">
                {typeof summary.sales_today_total === 'string' ? summary.sales_today_total : `$${(summary.sales_today_total || 0).toFixed(2)}`}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Transacciones de hoy</p>
              <p className="text-2xl font-semibold text-slate-900">{summary.transactions_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-yellow-50 p-2">
              <RotateCcw className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Devoluciones de hoy</p>
              <p className="text-2xl font-semibold text-slate-900">{summary.returns_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-50 p-2">
              <User className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Ticket promedio de hoy</p>
              <p className="text-2xl font-semibold text-slate-900">
                {typeof summary.average_ticket === 'string' ? summary.average_ticket : `$${(summary.average_ticket || 0).toFixed(2)}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <GlobalFilterBar
        show={['search', 'branch', 'status', 'seller', 'delivery', 'payment', 'bank', 'invoice', 'source', 'dates']}
        dateShortcuts={['today', 'yesterday', '1month', '1year', '1week', '5years']}
        values={filters}
        onChange={(changes) => {
          setFilters(prev => ({ ...prev, ...changes }))
          setPage(1)
        }}
        customOptions={{
          searchPlaceholder: "Buscar ticket, cliente o medicamento...",
          statusOptions: [
            { value: 'COMPLETED', label: 'Completadas' },
            { value: 'PARTIALLY_REFUNDED', label: 'Devolución Parcial' },
            { value: 'REFUNDED', label: 'Devolución Total' }
          ],
          banksOptions: banks.map(b => b.name)
        }}
        onClearFilters={() => {
          setFilters({
            search: '',
            branch: '',
            status: 'all',
            seller: '',
            delivery: '',
            payment: 'all',
            bank: 'all',
            invoice: 'all',
            source: 'all',
            startDate: '',
            endDate: ''
          })
          setPage(1)
        }}
        hasActiveFilters={
          !!filters.search || !!filters.branch || filters.status !== 'all' || !!filters.seller ||
          !!filters.delivery || filters.payment !== 'all' || filters.bank !== 'all' || filters.invoice !== 'all' || filters.source !== 'all'
        }
      />

      {/* Sales Table */}
      {isLoading ? (
        <TableSkeleton columns={8} rows={5} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Ticket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Fecha y Hora
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Vendedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Facturación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Origen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {salesList.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                      No se encontraron ventas con los filtros actuales.
                    </td>
                  </tr>
                ) : (
                  salesList.map((sale) => (
                    <tr key={sale.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900" title={sale.ticket_number || String(sale.id)}>
                            {formatShortTicket(sale.ticket_number || sale.id)}
                          </span>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
                            onClick={async (e) => {
                              e.stopPropagation()
                              const success = await copyToClipboard(String(sale.ticket_number || sale.id))
                              if (success) {
                                toast.success('Ticket copiado al portapapeles')
                              } else {
                                toast.error('No se pudo copiar el ticket')
                              }
                            }}
                            title="Copiar ticket completo"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-700 whitespace-nowrap">
                        <div className="font-medium">{sale.date}</div>
                        <div className="text-xs text-slate-500">{sale.time}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        <div className="font-medium text-slate-900 truncate max-w-[200px]" title={sale.customer}>{sale.customer}</div>
                        {sale.deliveryPerson && (
                          <div 
                            className="mt-1.5 flex w-fit max-w-[200px] items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-2.5 py-0.5 text-[11px] font-medium text-primary"
                            title={`Repartidor: ${sale.deliveryPerson}`}
                          >
                            <Truck className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{sale.deliveryPerson}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-700 whitespace-nowrap">{sale.seller}</td>
                      <td className="px-6 py-4 font-semibold text-slate-900">
                        ${sale.total.toFixed(2)}
                      </td>
                      <td className="px-6 py-4">
                        {sale.invoiced ? (
                          <Badge className="bg-green-100 text-green-700">
                            Facturada
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700">
                            Sin facturar
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Badge className="bg-slate-100 text-slate-700">POS</Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={getStatusColor(sale.status)}>
                          {getStatusLabel(sale.status)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openDetailModal(sale)}
                            className="rounded p-1 hover:bg-slate-100"
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4 text-slate-600" />
                          </button>
                          <button
                            onClick={() => openDetailModal(sale, true)}
                            className="rounded p-1 hover:bg-slate-100"
                            title="Reimprimir ticket"
                          >
                            <Printer className="h-4 w-4 text-slate-600" />
                          </button>
                          {!sale.invoiced && sale.status === 'COMPLETED' && (
                            <button
                              onClick={() => openInvoiceModal(sale)}
                              className="rounded p-1 hover:bg-slate-100"
                              title="Generar factura"
                            >
                              <FileText className="h-4 w-4 text-primary" />
                            </button>
                          )}

                          {/* Descargar: si ya está facturada */}
                          {sale.invoiced && (
                            <button
                              onClick={() => openDownloadModal(sale)}
                              className="rounded p-1 hover:bg-slate-100"
                              title="Descargar factura (PDF/XML)"
                            >
                              <Download className="h-4 w-4 text-green-600" />
                            </button>
                          )}

                          {/* Cancelar CFDI standalone — solo si está facturada y el CFDI no fue cancelado */}
                          {sale.invoiced && sale.cfdi_id && !(sale as any).cfdi_cancelled_at && (
                            <button
                              onClick={() => setCancelCfdiTarget({ cfdiId: sale.cfdi_id!, saleId: sale.id })}
                              className="rounded p-1 hover:bg-red-50"
                              title="Cancelar CFDI ante el SAT"
                            >
                              <XCircle className="h-4 w-4 text-red-400 hover:text-red-600" />
                            </button>
                          )}

                          {sale.status === 'COMPLETED' && (
                            <button
                              onClick={() => openReturnModal(sale, 'partial')}
                              className="rounded p-1 hover:bg-slate-100"
                              title="Procesar devolución"
                            >
                              <RotateCcw className="h-4 w-4 text-orange-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <GlobalPaginator
            totalItems={totalItems}
            itemsPerPage={10}
            currentPage={currentPage}
            from={salesData?.from}
            to={salesData?.to}
            onPageChange={(p) => setPage(p)}
          />
        </div>

      )}

      {/* Invoice Modal */}
      <InvoiceSaleModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        sale={selectedSale}
      />

      {/* Download Invoice Modal */}
      <DownloadInvoiceModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        sale={selectedSale}
      />

      {/* Global Invoice Wizard */}
      <GlobalInvoiceWizard
        isOpen={showGlobalInvoiceWizard}
        onClose={() => setShowGlobalInvoiceWizard(false)}
        onSuccess={() => setShowGlobalInvoiceWizard(false)}
      />

      {/* Invoices History Modal */}
      <InvoicesHistoryModal
        isOpen={showInvoicesHistoryModal}
        onClose={() => setShowInvoicesHistoryModal(false)}
      />

      {/* Detail Modal */}
      <SaleDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false)
          setPrintQueue(null)
        }}
        sale={selectedSale}
        onOpenReturnModal={openReturnModal}
        onOpenCancelModal={openCancelModal}
        autoPrint={printQueue !== null && selectedSale?.id === printQueue}
        onPrintComplete={() => setPrintQueue(null)}
      />

      {/* Return Modal */}
      <ReturnSaleModal
        isOpen={showReturnModal}
        onClose={() => setShowReturnModal(false)}
        sale={selectedSale}
        initialType={returnType}
      />

      {/* Cancel Modal */}
      <CancelSaleModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        sale={selectedSale}
      />

      {/* Cancel CFDI standalone Modal */}
      {cancelCfdiTarget && (
        <CancelCfdiModal
          isOpen={!!cancelCfdiTarget}
          onClose={() => setCancelCfdiTarget(null)}
          cfdiId={cancelCfdiTarget.cfdiId}
          saleId={cancelCfdiTarget.saleId}
          onSuccess={() => setCancelCfdiTarget(null)}
        />
      )}
    </div>
  )
}
